import type { ValueOf } from 'type-fest';

import { ERROR_BAR, SUCCESS_BAR, WARNING_BAR } from 'components/Common/NoticeBar/BannerBar';

type Empty = Record<string, never>;

export enum PayWallTiers {
  TRIAL = 'trial',
  STARTER = 'starter',
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  EXTRA_LARGE = 'extra large',
  EXTRA_LARGE_UNLIMITED = 'super large',
}

export const PayWallVariant = {
  Core: 'core',
  MaxModal: 'max_modal',
};

export type PayWallAvailablePlans = Record<PayWallTiers, PayWallPlan> | Empty;

export type PayWallTier =
  | (Omit<PayWallPlan, 'feature_headers' | 'plan_descriptions'> & {
      feature_descriptions: PayWallDescriptionItem[];
    })
  | Empty;

export type PayWallDescriptionItem = {
  title: string;
  description?: string;
};

export type PayWallFeature = {
  display_name: string;
  name: string;
  price: number;
  quantifiable: boolean;
  quantity: number | undefined;
};

export type PayWallGroup = {
  available_plans: number[];
  plan_descriptions: PayWallDescriptionItem[];
};

export type PayWallGroupIncrementalFeatures = PayWallGroup & {
  plan_descriptions_incremental: PayWallDescriptionItem[];
  addons_descriptions: PayWallDescriptionItem[];
};

export type PayWallPlan =
  | {
      additional_employee_rate: number;
      discount_label: string;
      display_name?: string;
      feature_headers: string[];
      id: number;
      number_of_free_seats: number;
      plan_descriptions: string[];
      plan_type: string;
      rate_flat_6_months: string | null;
      rate_flat_6_months_regular: string | null;
      rate_flat_annual: string;
      rate_flat_annual_regular: string;
      rate_flat_monthly: string;
      rate_flat_monthly_regular: string;
      tier: keyof PayWallAvailablePlans;
      available_features: PayWallFeature[];
      features: string[];
    }
  | Empty;

export type PayWallActivePlan =
  | (PayWallPlan & {
      active: boolean;
      can_add_employee: boolean;
      can_add_additional_employee: boolean;
    })
  | Empty;

export type PayWallOrganizationAddress =
  | {
      street: string;
      street_line_2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
      latitude: unknown;
      longitude: unknown;
    }
  | Empty;

export type PayWallOrgFeature = {
  id?: number;
  display_name: string;
  name: string;
  monthly_price?: string;
  six_month_price?: string;
  annual_price?: string;
  price: number;
  quantifiable: boolean;
  quantity: number | undefined;
};

type PayWallCreditCard = {
  brand: string;
  number: string;
};

export type PayWallAdditionalUsers = {
  count: number;
  amount: number;
  minimum: number;
  unlimited: boolean;
};

type PayWallPlanSummaryPreview =
  | {
      activation_fee_to_display: string;
      additional_features: PayWallOrgFeature[];
      additional_users: PayWallAdditionalUsers;
      applied_promos: string[];
      credit_amount: string;
      discount_amount: string;
      plan_amount: string;
      plan_type: string;
      prorated_balance: string;
      prorated_subtotal: string;
      recurring_amount: string;
      recurring_amount_gross: string;
      recurring_sales_tax: string;
      renewal_date: string;
      sales_tax_due_today: string;
      sales_tax_rate: string;
      skip_setup_fee: null;
      total_due_today: string;
      total_due_today_gross: string;
      when_to_apply: string;
      tax_calculated: boolean;
      valid_tax_address: boolean;
    }
  | Empty;

type BannerType = typeof SUCCESS_BAR | typeof WARNING_BAR | typeof ERROR_BAR;

export const PayWallDuration = {
  Monthly: 'monthly',
  SixMonths: '6_months',
  Annual: 'annual',
};

export type PayWallState = {
  planGroups: PayWallGroup[];
  tiers: PayWallTier[];
  availablePlans: PayWallAvailablePlans;
  plans: PayWallPlan[];
  availableFeatures: PayWallOrgFeature[] | never[];
  selectedPlan: PayWallActivePlan;
  originalAdditionalUsers: number | null;
  originalCurrentPlan: PayWallActivePlan;
  originalSelectedDuration: string;
  originalOrgFeatures: PayWallOrgFeature[];
  selectedDuration: ValueOf<typeof PayWallDuration> | '';
  creditCard: PayWallCreditCard | null;
  nameOnCard: string;
  organizationAddress: PayWallOrganizationAddress;
  orgFeatures: PayWallOrgFeature[];
  upgradePlanAmount: number;
  existingUsersCount: number | null;
  baseUrl: string;
  stripeKey: string | null;
  onPaymentSuccess: () => void;
  billUpToDate: string;
  trialUpgradeMessage: string;
  planSummaryPreview: PayWallPlanSummaryPreview;
  bannerType: BannerType;
  bannerMessage: string | null;
  uiOverrideBannerMessage: string | null;
  isUpsellDialogOpen: boolean;
  spinnerState: boolean;
  currentStep: string;
  isProImpersonated: boolean;
  required: string;
  variant: ValueOf<typeof PayWallVariant>;
  showDialog: boolean;
  feature: string;
  confirmDialogOpen: boolean;
  billingAddressDialogOpen: boolean;
  showCreditCard: boolean;
  additionalUsers: number | undefined;
  additionalUserMonthlyRate: string;
};

type EmployeeState = {
  permissions: string[];
  roles: string[];
  user_id: number;
  first_name: string;
  last_name: string;
};

export type PayWallFullState = {
  payWall: PayWallState;
  employee?: EmployeeState;
  mobile: boolean;
};
